import { useEffect, useState } from "react";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { MagnifyIcon } from "../../icons";
import { RecordTable, compareRowsBy } from "./RecordTable";
import { RecordCards } from "./RecordCards";
import { TablePagination, getPageRange } from "./TablePagination";
import { TabbedTablesProps } from "./TabbedTables.types";
import { useResponsiveClassName } from "./forcedBreakpoint";
import { FilterBar } from "../../lists/FilterBar";
import { ListFilterValues } from "../../lists/listFilters.types";
import { emptyFilterValues, rowMatchesFilters } from "../../lists/listFilterValues";

const DEFAULT_PAGE_SIZE = 10;
/** Absorbs the slack left by the data columns, which is what right-aligns the icons. */
const ACTIONS_COLUMN_KEY = "__actions";

export function TabbedTables({
  tables,
  relatedEntities,
  activeKey: controlledKey,
  onTabSelect,
  title,
  className = "",
}: TabbedTablesProps) {
  const [ownKey, setOwnKey] = useState(tables[0]?.key);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<{
    key: string;
    dir: "asc" | "desc";
  } | null>(null);
  // Driven from outside when the designer's properties panel names a tab, else its own.
  const activeKey = controlledKey ?? ownKey;

  // What the reader has set, over every filter being empty.
  const [pickedFilters, setPickedFilters] = useState<ListFilterValues>({});

  // Search, filters, page and sort belong to the tab being viewed, so they reset with it.
  useEffect(() => {
    setSearchTerm("");
    setPickedFilters({});
    setPage(1);
    setSort(null);
  }, [activeKey]);
  // Shared by every tab once chosen; until then each uses its own configured default.
  const [chosenPageSize, setChosenPageSize] = useState<number | null>(null);
  const pageSizeId = `${activeKey ?? "table"}-page-size`;
  const activeTable = tables.find((table) => table.key === activeKey);

  // Its own state either way, so an uncontrolled strip still works; the designer hears
  // about it as well and answers by naming the same tab back.
  const handleTabClick = (key: string) => {
    setOwnKey(key);
    onTabSelect?.(key);
  };

  const handleSortChange = (key: string, dir: "asc" | "desc") => {
    setSort({ key, dir });
    setPage(1);
  };

  const handlePageSizeChange = (size: number) => {
    setChosenPageSize(size);
    setPage(1);
  };

  const activeSort =
    sort ??
    (activeTable?.defaultSortKey
      ? {
          key: activeTable.defaultSortKey,
          dir: activeTable.defaultSortDir ?? "asc",
        }
      : null);

  const activeRelated = relatedEntities?.find(
    (related) => related.entityName === activeTable?.entityName
  );
  const allRows =
    activeRelated
      ?.entities.map((entity) => ({
        id: entity.Id,
        name: entity.Name,
        ...entity.Fields,
      })) ?? [];

  const tableFilters = activeTable?.filters ?? [];
  const filterValues = { ...emptyFilterValues(tableFilters), ...pickedFilters };
  const changeFilters = (patch: ListFilterValues) => {
    setPickedFilters((previous) => ({ ...previous, ...patch }));
    setPage(1);
  };

  const excluded = activeTable?.filterRow
    ? allRows.filter(activeTable.filterRow)
    : allRows;
  // Filters read a list's row, which names the record `Id` and `Name`.
  const rows =
    tableFilters.length > 0
      ? excluded.filter((row) =>
          rowMatchesFilters(
            { ...row, Id: row.id, Name: row.name },
            tableFilters,
            filterValues
          )
        )
      : excluded;

  const searchFields = activeTable?.searchFields;
  const filteredRows =
    activeTable?.searchable && searchTerm
      ? rows.filter((row) =>
          (searchFields
            ? searchFields.map(
                (field) => (row as Record<string, unknown>)[field]
              )
            : Object.values(row)
          ).some((value) =>
            String(value ?? "").toLowerCase().includes(searchTerm.toLowerCase())
          )
        )
      : rows;

  // The configured secondary breaks ties the primary leaves, whether the primary
  // is the configured one or a column the user clicked.
  const secondarySort = activeTable?.secondarySortKey
    ? {
        key: activeTable.secondarySortKey,
        dir: activeTable.secondarySortDir ?? ("asc" as const),
      }
    : null;

  const sortedRows = activeSort
    ? [...filteredRows].sort((a, b) =>
        compareRowsBy(a, b, [activeSort, secondarySort])
      )
    : filteredRows;

  const pageSize =
    chosenPageSize ?? activeTable?.pageSize ?? DEFAULT_PAGE_SIZE;
  const { from, to } = getPageRange(filteredRows.length, page, pageSize);
  const pagedRows = sortedRows.slice(from, to);

  const rowActions = activeTable?.rowActions;
  const columns =
    activeTable && rowActions
      ? [
          ...activeTable.columns,
          {
            key: ACTIONS_COLUMN_KEY,
            label: "",
            sortable: false,
            resizable: false,
            minWidth: 60,
            accessor: (row: Record<string, unknown>) => rowActions(row),
          },
        ]
      : activeTable?.columns ?? [];
  const toolbarActions = activeTable?.toolbarActions;
  // Tabs from tablet up, a select on a phone - forced to one or the other in the
  // designer's previews, which are narrow boxes in a wide window.
  const tabsDisplay = useResponsiveClassName({
    viewport: "d-none d-md-flex",
    mobile: "d-none",
    tablet: "d-flex",
  });
  const selectDisplay = useResponsiveClassName({
    viewport: "d-md-none",
    mobile: "",
    tablet: "d-none",
  });

  // One tab is just the table's name, over it. The designer keeps the tab, as it is what
  // selects the table there.
  const onlyTab = tables.length === 1 && !onTabSelect;
  const emptyMessage = activeRelated?.failed
    ? `${activeTable?.label ?? "These records"} couldn't be loaded.`
    : activeTable?.emptyMessage;

  // One cards tab is a heading over the cards and nothing else - not a page section,
  // which is itself drawn as a card, and not a panel with a tab strip.
  if (tables.length === 1 && activeTable?.layout === "cards") {
    return (
      <section className={className}>
        <div className="d-flex align-items-center gap-3 mb-3">
          <h2 className="page-section__title fw-semibold h5 mb-0">
            {activeTable.label}
          </h2>
          {activeTable.toolbarActions}
        </div>
        <RecordCards
          columns={activeTable.columns}
          rows={sortedRows}
          emptyMessage={emptyMessage}
          emptyTone={activeRelated?.failed ? "danger" : "info"}
          rowActions={activeTable.rowActions}
          rowKey={(row) => String(row.id)}
        />
      </section>
    );
  }

  return (
    <div
      className={classNames(
        "page-tabbed-tables",
        { "page-tabbed-tables--flush": activeTable?.flush },
        className
      )}
    >
      {onlyTab && activeTable && (
        <h2 className="page-section__title fw-semibold h5 mb-3">
          {activeTable.label}
        </h2>
      )}
      {!onlyTab && title && (
        <h2 className="page-section__title fw-semibold h5 mb-3">{title}</h2>
      )}
      <div
        className={classNames("page-tabbed-tables__panel", {
          "mx-n3-5 mb-n3-5 rounded-bottom": activeTable?.flush,
          // The advanced filters hang below the bar, over the table.
          "overflow-visible": tableFilters.length > 0,
        })}
      >
        {!onlyTab && (
          <div className={classNames("underline-tabs", tabsDisplay)}>
            {tables.map((table) => (
              <button
                key={table.key}
                type="button"
                className={classNames("underline-tabs__tab", {
                  "underline-tabs__tab--active": table.key === activeKey,
                })}
                onClick={() => handleTabClick(table.key)}
              >
                {table.label}
              </button>
            ))}
          </div>
        )}
        {!onlyTab && (
          <div className={classNames("page-tabbed-tables__tabs-select", selectDisplay)}>
            <select
              className="form-select"
              aria-label="Table"
              value={activeKey}
              onChange={(event) => handleTabClick(event.target.value)}
            >
              {tables.map((table) => (
                <option key={table.key} value={table.key}>
                  {table.label}
                </option>
              ))}
            </select>
          </div>
        )}
        {tableFilters.length > 0 && (
          <FilterBar
            filters={tableFilters}
            filterValues={filterValues}
            setFilterValue={(key: string, value: string | boolean) =>
              changeFilters({ [key]: value })
            }
            setFilterValues={changeFilters}
            clearFilters={() => {
              setPickedFilters({});
              setPage(1);
            }}
          />
        )}
        {activeTable && (activeTable.searchable || toolbarActions) && (
          <div className="page-tabbed-tables__search">
            {activeTable.searchable && (
              <div className="page-tabbed-tables__search-box">
                <FontAwesomeIcon icon={MagnifyIcon} />
                <input
                  type="search"
                  id={`${activeTable.key}-search`}
                  name={`${activeTable.key}-search`}
                  aria-label={`Search ${activeTable.label.toLowerCase()}`}
                  placeholder={`Search ${activeTable.label.toLowerCase()}`}
                  value={searchTerm}
                  onChange={(event) => {
                    setSearchTerm(event.target.value);
                    setPage(1);
                  }}
                />
              </div>
            )}
            {toolbarActions && (
              <div className="page-tabbed-tables__toolbar-actions">
                {toolbarActions}
              </div>
            )}
          </div>
        )}
        {activeTable?.layout === "cards" && (
          <div className="p-3">
            <RecordCards
              columns={activeTable.columns}
              rows={pagedRows}
              emptyMessage={emptyMessage}
              emptyTone={activeRelated?.failed ? "danger" : "info"}
              rowActions={activeTable.rowActions}
              rowKey={(row) => String(row.id)}
            />
          </div>
        )}
        {activeTable && activeTable.layout !== "cards" && (
          <div
            className={classNames("page-tabbed-tables__table-scroll", {
              "min-h-0 mt-0": activeTable.flush,
            })}
          >
            <RecordTable
              key={activeTable.key}
              className={activeTable.flush ? "mb-0" : undefined}
              columns={columns}
              rows={pagedRows}
              emptyMessage={emptyMessage}
              sortKey={activeSort?.key ?? null}
              sortDir={activeSort?.dir}
              onSortChange={handleSortChange}
            />
          </div>
        )}
        <TablePagination
          total={filteredRows.length}
          page={page}
          pageSize={pageSize}
          pageSizeId={pageSizeId}
          showPageSize={!activeTable?.fixedPageSize}
          onPageChange={setPage}
          onPageSizeChange={handlePageSizeChange}
        />
      </div>
    </div>
  );
}
